import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";

const STATUS_STYLE = {
  qa_review: "bg-orange-100 text-orange-900",
  approved: "bg-[#2C5A2E]/10 text-[#2C5A2E]",
  returned: "bg-red-100 text-red-900",
};

export default function QaQueue() {
  const [reports, setReports] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/qa/queue").then((r) => setReports(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const pending = (reports || []).filter((r) => r.status === "qa_review");
  const reviewed = (reports || []).filter((r) => r.status !== "qa_review");

  const Row = ({ r }) => (
    <tr className="border-b border-[#E5E5DF] last:border-0 hover:bg-[#FAFAF8] transition-colors cursor-pointer"
      onClick={() => navigate(`/admin/qa/${r.case_id}`)} data-testid={`qa-row-${r.case_id}`}>
      <td className="px-6 py-4 font-semibold">{r.client_name || "—"}</td>
      <td className="px-6 py-4 hidden md:table-cell text-[#5C5C57]">{r.product}</td>
      <td className="px-6 py-4 hidden md:table-cell text-[#5C5C57]">{r.clinician_name}</td>
      <td className="px-6 py-4">
        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[r.status]}`} data-testid={`qa-status-${r.case_id}`}>
          {r.status === "qa_review" ? "Awaiting QA" : r.status}
        </span>
      </td>
      <td className="px-6 py-4 text-right"><ArrowRight className="h-4 w-4 text-[#7E9B82] inline" /></td>
    </tr>
  );

  return (
    <DashboardShell title="QA review" subtitle="Every report passes the quality-assurance checklist before delivery to the client.">
      {reports === null ? (
        <div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" />
      ) : (
        <div className="space-y-10">
          <section>
            <p className="label-caps text-[#7E9B82] mb-4">Awaiting review ({pending.length})</p>
            {pending.length === 0 ? (
              <div className="card-warm p-10 text-center" data-testid="qa-empty"><p className="text-sm text-[#5C5C57]">Nothing waiting for review.</p></div>
            ) : (
              <div className="card-warm overflow-hidden" data-testid="qa-pending-table">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C5C57] border-b border-[#E5E5DF]">
                    <th className="px-6 py-4">Client</th><th className="px-6 py-4 hidden md:table-cell">Evaluation</th>
                    <th className="px-6 py-4 hidden md:table-cell">Clinician</th><th className="px-6 py-4">Status</th><th className="px-6 py-4"></th>
                  </tr></thead>
                  <tbody>{pending.map((r) => <Row key={r.case_id} r={r} />)}</tbody>
                </table>
              </div>
            )}
          </section>
          {reviewed.length > 0 && (
            <section>
              <p className="label-caps text-[#7E9B82] mb-4">Previously reviewed</p>
              <div className="card-warm overflow-hidden" data-testid="qa-reviewed-table">
                <table className="w-full text-sm">
                  <tbody>{reviewed.map((r) => <Row key={r.case_id} r={r} />)}</tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </DashboardShell>
  );
}
